"use client"

import { usePathname } from "next/navigation"
import { ArrowRight } from "lucide-react"

import { useScrolledDown } from "@/components/layout/floating-actions"
import { Money } from "@/components/shared/money"
import { ButtonLink } from "@/components/ui/button"
import { buyNowHref, useBuySelection } from "@/features/catalog/hooks/use-buy-selection"
import { cn } from "@/lib/utils"

/**
 * Phones and tablets: a price and one button, held to the foot of the screen. Near-opaque, not
 * blurred: a backdrop blur janks scrolling on phones. `data-sticky-bar` lifts the floating
 * buttons and the cookie card clear of it, so only a shown bar carries it.
 */
export function BuyBar({
  price,
  freeShipping,
  hidden = false,
  children,
}: {
  price: string
  freeShipping: boolean | null
  /** Slid away below the screen, as on the home page's first screen. */
  hidden?: boolean
  /** The button. */
  children: React.ReactNode
}) {
  return (
    <div
      data-sticky-bar={hidden ? undefined : ""}
      inert={hidden}
      className={cn(
        "bg-void/97 sticky bottom-0 z-40 flex items-center gap-3 border-t border-white/10 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] transition-[translate,opacity] duration-300 lg:hidden",
        hidden && "pointer-events-none translate-y-full opacity-0",
      )}
    >
      <div className="shrink-0">
        <Money value={price} className="font-display text-bone text-[26px] leading-none" />
        <div className="text-dim mt-1 font-mono text-[10px] tracking-[0.1em] uppercase">
          {freeShipping ? "Free shipping" : "Shipping by pincode"}
        </div>
      </div>
      {children}
    </div>
  )
}

/** Tighter below 360px, where the button would otherwise overhang. */
export const BAR_BUTTON = "flex-1 max-[359px]:px-4"

/**
 * The product page's bar. Buy now goes straight to checkout with the buy panel's colourway and
 * quantity, leaving the cart untouched.
 */
export function StickyBuyBar({
  productSlug,
  price,
  defaultColourway,
}: {
  productSlug: string
  price: string
  defaultColourway: string
}) {
  const colourway = useBuySelection((s) => s.colourway) ?? defaultColourway
  const qty = useBuySelection((s) => s.qty)
  const picked = useBuySelection((s) => s.price) ?? price
  const freeShipping = useBuySelection((s) => s.freeShipping)

  return (
    <BuyBar price={picked} freeShipping={freeShipping}>
      <ButtonLink
        href={buyNowHref(colourway, qty, productSlug)}
        variant="primary"
        size="md"
        full
        className={BAR_BUTTON}
      >
        Buy now
        <ArrowRight className="size-4" strokeWidth={2.4} />
      </ButtonLink>
    </BuyBar>
  )
}

// Pages with a bar of their own, and checkout, which has its own way to pay.
const OWN_BAR = ["/product/", "/combos/", "/checkout"]

/**
 * Every other storefront page: the featured mount's price, and the way to its page. On the home
 * page only once it is scrolled, like the WhatsApp button: its first screen has Grab yours.
 */
export function SiteBuyBar({
  productSlug,
  price,
  freeShipping,
}: {
  productSlug: string
  price: string
  freeShipping: boolean
}) {
  const pathname = usePathname()
  const scrolled = useScrolledDown()
  if (OWN_BAR.some((start) => pathname.startsWith(start))) return null
  return (
    <BuyBar price={price} freeShipping={freeShipping} hidden={pathname === "/" && !scrolled}>
      <ButtonLink
        href={`/product/${productSlug}`}
        variant="primary"
        size="md"
        full
        className={BAR_BUTTON}
      >
        Buy now
        <ArrowRight className="size-4" strokeWidth={2.4} />
      </ButtonLink>
    </BuyBar>
  )
}
